import { useEffect, useState } from 'react';
import { addKlantEigenItem } from '../../lib/upsell';

interface KlantEigenItemDialogProps {
  isOpen: boolean;
  workOrderId: string | null;
  onClose: () => void;
  onAdded: () => void;
}

const SNELKEUZE = ['Ondertapijt', 'Spanbanden'];

/**
 * Tagt een LOS, KLANT-EIGEN item à €0 op order-niveau (ondertapijt, spanbanden, …):
 * niet op de factuur, gaat netjes mee terug. Type via snelknoppen + 'Overig' (vrij
 * veld). Bij succes toont het het WI-nummer om het fysieke item te labelen.
 */
export function KlantEigenItemDialog({ isOpen, workOrderId, onClose, onAdded }: KlantEigenItemDialogProps) {
  const [keuze, setKeuze] = useState<string>('Ondertapijt');
  const [overigTekst, setOverigTekst] = useState('');
  const [lengteCm, setLengteCm] = useState('');
  const [breedteCm, setBreedteCm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ nummer: string; naam: string } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setKeuze('Ondertapijt');
      setOverigTekst('');
      setLengteCm('');
      setBreedteCm('');
      setBusy(false);
      setError(null);
      setCreated(null);
    }
  }, [isOpen]);

  if (!isOpen || !workOrderId) return null;

  const isOverig = keuze === 'Overig';
  const omschrijving = (isOverig ? overigTekst : keuze).trim();

  async function handleConfirm() {
    if (!workOrderId) return;
    if (!omschrijving) {
      setError(isOverig ? 'Vul in wat het is.' : 'Kies een type.');
      return;
    }
    const l = lengteCm.trim() ? Number(lengteCm) : undefined;
    const b = breedteCm.trim() ? Number(breedteCm) : undefined;
    if ((l === undefined) !== (b === undefined)) {
      setError('Vul lengte én breedte in, of laat beide leeg.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await addKlantEigenItem({ workOrderId, omschrijving, lengteCm: l, breedteCm: b });
      if (!res.success) {
        setError(res.message || 'Toevoegen mislukt');
        return;
      }
      setCreated({ nummer: res.childItemName || '—', naam: omschrijving });
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Los item taggen</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Klant-eigen item, €0 — komt niet op de factuur en gaat mee terug.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0" aria-label="Sluiten">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4">
          {created ? (
            <div className="text-center py-4">
              <p className="text-sm text-slate-500">{created.naam} toegevoegd als:</p>
              <p className="text-3xl font-bold text-indigo-700 mt-2 font-mono">{created.nummer}</p>
              <p className="text-xs text-slate-500 mt-3">Label het item met dit nummer en pak het apart in.</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">{error}</div>
              )}
              <p className="text-xs text-slate-600 mb-2">Wat is het?</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {[...SNELKEUZE, 'Overig'].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setKeuze(opt)}
                    className={
                      'text-sm px-3 py-1.5 rounded-full border ' +
                      (keuze === opt
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-300')
                    }
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {isOverig && (
                <input
                  type="text"
                  value={overigTekst}
                  disabled={busy}
                  onChange={(e) => setOverigTekst(e.target.value)}
                  className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white disabled:opacity-60 mb-3"
                  placeholder="bijv. 2 kussens, hondenmand…"
                  autoFocus
                />
              )}
              <p className="text-xs text-slate-500 mb-1">Afmetingen optioneel (laat leeg als onbekend).</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-slate-600">
                  Lengte (cm)
                  <input
                    type="number" min={30} max={800} value={lengteCm} disabled={busy}
                    onChange={(e) => setLengteCm(e.target.value)}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white disabled:opacity-60"
                    placeholder="bijv. 200"
                  />
                </label>
                <label className="text-xs text-slate-600">
                  Breedte (cm)
                  <input
                    type="number" min={30} max={800} value={breedteCm} disabled={busy}
                    onChange={(e) => setBreedteCm(e.target.value)}
                    className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white disabled:opacity-60"
                    placeholder="bijv. 150"
                  />
                </label>
              </div>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          {created ? (
            <button type="button" onClick={onClose} className="text-sm px-4 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700">
              Klaar
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
                Annuleren
              </button>
              <button type="button" disabled={busy} onClick={handleConfirm} className="text-sm px-4 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300">
                {busy ? 'Bezig…' : 'Toevoegen (€0)'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
